import type { ReactElement } from 'react';
import {
  BriefcaseBusiness,
  ClipboardList,
  Code2,
  FileSignature,
  HardDrive,
  Library,
  Mail,
  Plug,
  Route,
  Settings,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PageWithSidebar } from '@/components/shell/PageWithSidebar';
import { useSetBreadcrumbs } from '@/components/shell/ShellContext';
import { Sidebar, type SidebarGroup } from '@/components/shell/Sidebar';

export function AdminPage(): ReactElement {
  const { t } = useTranslation();
  // PTV2-1383: OAuth credentials + auth providers moved to the global platform
  // console (they are cluster-wide, platform:MANAGE-gated identity config).

  const groups: readonly SidebarGroup[] = [
    {
      key: 'admin',
      heading: t('routes.admin.sidebar.heading'),
      items: [
        {
          key: 'settings',
          label: t('routes.admin.sidebar.settings'),
          to: '/admin/settings',
          icon: <Settings className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'configs',
          label: t('routes.admin.sidebar.configs'),
          to: '/admin/configs',
          icon: <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'fhir-integrations',
          label: t('routes.admin.sidebar.fhirIntegrations'),
          to: '/admin/fhir-integrations',
          icon: <Plug className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'users',
          label: t('routes.admin.sidebar.users'),
          to: '/admin/users',
          icon: <Users className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'consent-types',
          label: t('routes.admin.sidebar.consentTypes'),
          to: '/admin/consent-types',
          icon: <FileSignature className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'apis',
          label: t('routes.admin.sidebar.apis'),
          to: '/admin/apis',
          icon: <Code2 className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'vocabularies',
          label: t('routes.admin.sidebar.vocabularies'),
          to: '/admin/vocabularies',
          icon: <Library className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'questionnaires',
          label: t('routes.admin.sidebar.questionnaires'),
          to: '/admin/questionnaires',
          icon: <ClipboardList className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'email',
          label: t('routes.admin.sidebar.email'),
          to: '/admin/email',
          icon: <Mail className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 's3Configs',
          label: t('routes.admin.sidebar.s3Configs'),
          to: '/admin/s3-configs',
          icon: <HardDrive className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'care-pathways',
          label: t('routes.admin.sidebar.carePathways'),
          to: '/admin/care-pathways',
          icon: <Route className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'jobs',
          label: t('routes.admin.sidebar.jobs'),
          to: '/admin/jobs',
          icon: <BriefcaseBusiness className="h-4 w-4" aria-hidden="true" />,
        },
      ],
    },
    // PTV2-1383: the "cluster" group (Discovery/Build-info/Logs/Database) moved to the global
    // platform pane (PlatformPage) — those are cluster-wide infra, not tenant admin.
  ];

  // PTV2-1383: top-bar breadcrumbs "Administration › <section>", reusing the sidebar's own labels.
  const { pathname } = useLocation();
  const activeSection = groups
    .flatMap((group) => group.items)
    .find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
  useSetBreadcrumbs([
    { label: t('shell.topNav.admin'), to: '/admin' },
    ...(activeSection === undefined ? [] : [{ label: activeSection.label }]),
  ]);

  return (
    <PageWithSidebar
      sidebar={<Sidebar groups={groups} />}
      mainAriaLabel={t('routes.admin.heading')}
    >
      <Outlet />
    </PageWithSidebar>
  );
}
